import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { CalendarOff, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { STATUS_LABELS, fmtEur } from "@/lib/quote";
import { EventsCalendar } from "@/components/admin/EventsCalendar";
import { StaffPanel } from "@/components/admin/StaffPanel";
import { ReservationsPanel } from "@/components/admin/ReservationsPanel";
import { SecurityPanel } from "@/components/admin/SecurityPanel";

const Panel = ({ title, children }) => (
  <div className="bg-[#1E293B] border border-slate-700 rounded-xl p-4">
    <h3 className="text-xs font-bold uppercase tracking-widest text-[#C85A32] mb-3">{title}</h3>
    {children}
  </div>
);
const inp = "bg-[#0F172A] border-slate-700 text-white h-9";

const CalendarioTab = () => {
  const [blocked, setBlocked] = useState({ manual: [], events: [] });
  const [newDate, setNewDate] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try { setBlocked((await api.get("/admin/blocked-dates")).data); } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <EventsCalendar />
      <ReservationsPanel />
      <Panel title="Blocca una data">
        <div className="flex gap-2 flex-wrap">
          <Input data-testid="block-date-input" type="date" className={`${inp} w-40`} value={newDate} onChange={(e) => setNewDate(e.target.value)} />
          <Input data-testid="block-reason-input" className={`${inp} flex-1 min-w-[160px]`} placeholder="Motivo (opzionale)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button data-testid="block-date-button" className="bg-[#C85A32] hover:bg-[#a94a28]" onClick={async () => {
            if (!newDate) return toast.error("Scegli una data");
            await api.post("/admin/blocked-dates", { date: newDate, reason });
            toast.success("Data bloccata"); setNewDate(""); setReason(""); load();
          }}><CalendarOff className="w-4 h-4 mr-1" /> Blocca</Button>
        </div>
      </Panel>
      <Panel title="Date occupate">
        <div className="space-y-1.5">
          {blocked.events.map((b, i) => (
            <div key={`e${i}`} className="flex items-center gap-2 text-sm py-1.5 border-b border-slate-700/50">
              <span className="font-mono-num font-bold">{b.date}</span>
              <span className="text-slate-400 text-xs">{b.reason}</span>
              <span className="ml-auto text-[10px] uppercase text-indigo-400 font-bold">evento</span>
            </div>
          ))}
          {blocked.manual.map((b) => (
            <div key={b.date} data-testid={`blocked-date-${b.date}`} className="flex items-center gap-2 text-sm py-1.5 border-b border-slate-700/50">
              <span className="font-mono-num font-bold">{b.date}</span>
              <span className="text-slate-400 text-xs">{b.reason || "Bloccata manualmente"}</span>
              <button data-testid={`unblock-${b.date}`} className="ml-auto text-slate-500 hover:text-rose-400" onClick={async () => { await api.delete(`/admin/blocked-dates/${b.date}`); load(); }}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {!blocked.manual.length && !blocked.events.length && <p className="text-sm text-slate-500">Nessuna data bloccata</p>}
        </div>
      </Panel>
      <StaffPanel />
    </div>
  );
};

const StatisticheTab = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  if (!stats) return null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ["Preventivi ricevuti", stats.total_quotes],
          ["Approvati / confermati", stats.approved_count],
          ["Valore approvato", fmtEur(stats.approved_value)],
          ["Valore medio", fmtEur(stats.avg_value)],
        ].map(([label, val]) => (
          <div key={label} className="bg-[#1E293B] border border-slate-700 rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
            <div data-testid={`stat-${label.replace(/\W/g, "")}`} className="font-mono-num text-2xl font-bold text-[#F0B429] mt-1">{val}</div>
          </div>
        ))}
      </div>
      <Panel title={`Conversione: ${stats.conversion_rate}%`}>
        <div className="space-y-2">
          {Object.entries(stats.by_status).map(([s, v]) => (
            <div key={s} className="flex items-center gap-2 text-sm">
              <span className="w-36">{STATUS_LABELS[s]}</span>
              <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-[#C85A32]" style={{ width: `${(v.count / stats.total_quotes) * 100}%` }} />
              </div>
              <span className="font-mono-num text-xs w-20 text-right">{v.count} · {fmtEur(v.value)}</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
};

export const ExtrasPanels = ({ tab }) => {
  if (tab === "calendario") return <CalendarioTab />;
  if (tab === "statistiche") return <StatisticheTab />;
  return <SecurityPanel />;
};
